import React from "react";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { makeStyles } from "tss-react/mui";
import { SubscriptionPrice } from "./components";
import { DomainContext } from "./useDomain";
import { AuthContext } from "../auth";
import OutstandingPaymentText from "./OutstandingPaymentText";
import { Domain, StripePlan } from "../lib/cnb/api/public";
import Box from "@mui/material/Box";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import CallToActionIcon from "@mui/icons-material/CallToAction";
import LayersIcon from "@mui/icons-material/Layers";
import ChatIcon from "@mui/icons-material/Chat";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ListAltIcon from "@mui/icons-material/ListAlt";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AnimationIcon from "@mui/icons-material/Animation";
import RuleIcon from "@mui/icons-material/Rule";
import Divider from "@mui/material/Divider";
import ScheduleIcon from "@mui/icons-material/Schedule";

const useStyles = makeStyles()((theme) => ({
  planColumns: {
    marginTop: theme.spacing(1),
    justifyContent: "center",

    "& > div > div": {
      padding: theme.spacing(1.5),
      borderColor: theme.palette.divider,
      borderStyle: "solid",
      borderWidth: 1,
      marginRight: -1,
      marginBottom: -1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",

      "&:first-of-type": {
        flexDirection: "column",
      },

      "&:last-child": {
        padding: theme.spacing(2.5),
      },

      "& svg": {
        marginRight: theme.spacing(0.5),
      },
    },
  },
}));

const ToggleButton: React.FC<{
  value: StripePlan["currency"];
  selected: StripePlan["currency"];
  onClick: (newValue: StripePlan["currency"]) => void;
}> = ({ value, selected, onClick, ...props }) => (
  <Button
    {...props}
    variant={selected === value ? "contained" : "outlined"}
    onClick={() => onClick(value)}
  >
    {value.toUpperCase()}
  </Button>
);

const SubscriptionPlan: React.FC = () => {
  const { classes } = useStyles();
  const { currentUser } = React.useContext(AuthContext);
  const [currency, setCurrency] = React.useState<StripePlan["currency"]>(
    (currentUser?.stripeDetails?.currency?.toUpperCase() as StripePlan["currency"]) ??
      "EUR",
  );

  const [checkoutError, setCheckoutError] = React.useState<string | null>(null);
  const { stripePlans, redirectToCheckout, wpInfo, currentDomain } =
    React.useContext(DomainContext);
  const userCurrency = currentUser?.stripeDetails?.currency?.toUpperCase();

  const plans = stripePlans?.filter(
    (plan) =>
      userCurrency == null ||
      plan.currency === currentUser?.stripeDetails?.currency?.toUpperCase(),
  );
  const proAnnualPrice = plans?.find(
    (p) => p.interval === "yearly" && p.currency === currency,
  )?.price;
  const proMonthlyPrice = plans?.find(
    (p) => p.interval === "monthly" && p.currency === currency,
  )?.price;
  const proAnnualTrialDays =
    plans?.find((p) => p.interval === "yearly" && p.currency === currency)
      ?.trialPeriodDays || 0;
  const proMonthlyTrialDays =
    plans?.find((p) => p.interval === "monthly" && p.currency === currency)
      ?.trialPeriodDays || 0;
  const handleCheckout = (interval: Domain["interval"]) => () => {
    redirectToCheckout(interval, currency).catch((e) => {
      setCheckoutError(e.message || "");
    });
  };

  const [showProFeatures, setShowProFeatures] = React.useState(false);

  if (wpInfo?.hasOutstandingInvoice()) {
    return <OutstandingPaymentText />;
  }
  return (
    <Grid container alignItems="center" direction="column">
      {userCurrency == null && (
        <ButtonGroup color="primary">
          <ToggleButton value="EUR" selected={currency} onClick={setCurrency} />
          <ToggleButton value="USD" selected={currency} onClick={setCurrency} />
        </ButtonGroup>
      )}

      <Typography
        variant="body2"
        align="center"
        color="text.secondary"
        sx={{ mt: 2, mb: 2 }}
      >
        Upgrade applies to {currentDomain?.name} only.
      </Typography>

      <Grid item container xs={12} className={classes.planColumns}>
        <Grid item container direction="column" xs={6}>
          <Grid item>
            <Typography variant="h6" align="center">
              Yearly plan
            </Typography>
            <SubscriptionPrice
              value={(proAnnualPrice ?? 0) / 12}
              currency={currency}
            />
            <Typography variant="body2" align="center">
              per month
            </Typography>
            <Typography variant="caption" align="center">
              (billed {currency === "EUR" ? "€" : "$"}
              {(proAnnualPrice ?? 0).toFixed(2)} annually)
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCheckout("yearly")}
            >
              {proAnnualTrialDays > 0 ? (
                <>Start {proAnnualTrialDays}-day free trial</>
              ) : (
                <>Upgrade</>
              )}
            </Button>
          </Grid>
        </Grid>
        <Grid item container direction="column" xs={6}>
          <Grid item>
            <Typography variant="h6" align="center">
              Monthly plan
            </Typography>
            <SubscriptionPrice
              value={proMonthlyPrice ?? 0}
              currency={currency}
            />
            <Typography variant="body2" align="center">
              per month
            </Typography>
            <Typography variant="caption" align="center">
              (billed monthly)
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCheckout("monthly")}
            >
              {proMonthlyTrialDays > 0 ? (
                <>Start {proMonthlyTrialDays}-day free trial</>
              ) : (
                <>Upgrade</>
              )}
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Grid item>
        <Button
          variant="text"
          color="primary"
          onClick={() => setShowProFeatures(!showProFeatures)}
          sx={{ mt: 2 }}
        >
          {showProFeatures ? "Hide" : "What's included in PRO?"}
        </Button>
      </Grid>

      {showProFeatures && (
        <Grid
          item
          xs={12}
          sx={{
            mt: 2,
            maxWidth: 600,
            width: "90%",
            backgroundColor: (theme) => theme.palette.grey[100],
            borderRadius: 2,
            p: 3,
          }}
        >
          <Typography
            variant="h5"
            align="center"
            gutterBottom
            sx={{
              fontWeight: "bold",
              color: (theme) => theme.palette.primary.main,
            }}
          >
            PRO Features
          </Typography>
          <Grid container spacing={2} sx={{ px: 2 }}>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <AllInclusiveIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2">Create 100+ buttons</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <MoreVertIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2">
                  Multiple buttons per page
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <ScheduleIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2">Button Scheduler</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <LayersIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2">Multi-action buttons</Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <ChatIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2">Live chat (beta)</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <WhatsAppIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2">WhatsApp widget</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <CallToActionIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2">Content Windows</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <ListAltIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2">Tally forms integration</Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <InsertPhotoIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2">Custom button images</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <AnimationIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2">Button animations</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <RuleIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2">
                  Advanced page and country rules
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      )}
      {checkoutError !== null && (
        <FormHelperText error>
          {checkoutError || "Server error: something went wrong."}
        </FormHelperText>
      )}
    </Grid>
  );
};

export default SubscriptionPlan;
